import { useEffect, useState } from "react";
import api, { formatError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { toast } from "sonner";
import { PackageCheck, Inbox } from "lucide-react";

const CONDITIONS = ["Working", "Non-Working", "Damaged"];
const PRIORITY_CHIP = {
  Urgent: "bg-rose-100 text-rose-800",
  High: "bg-amber-100 text-amber-800",
  Normal: "bg-sky-100 text-sky-800",
  Low: "bg-slate-100 text-slate-700",
};

export default function AssetManagementExtras({ canApprove }) {
  const [returns, setReturns] = useState([]);
  const [requests, setRequests] = useState([]);

  const [returnTarget, setReturnTarget] = useState(null);
  const [returnForm, setReturnForm] = useState({ condition: "Working", deduct_cost: false, deduct_amount: "", it_remark: "" });

  const [requestTarget, setRequestTarget] = useState(null);
  const [pickAssetId, setPickAssetId] = useState("");
  const [pickRemark, setPickRemark] = useState("");
  const [unassignedAssets, setUnassignedAssets] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadReturns = () => {
    if (!canApprove) return;
    api.get("/it-asset-return-requests")
      .then(({ data }) => setReturns(data || []))
      .catch(() => setReturns([]));
  };
  const loadRequests = () => {
    if (!canApprove) return;
    api.get("/asset-requests").then(({ data }) => setRequests((data || []).filter((r) => r.status === "pending"))).catch(() => setRequests([]));
  };

  useEffect(() => {
    loadReturns();
    loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canApprove]);

  // --- Return approval ---
  const openReturn = (a) => {
    setReturnTarget(a);
    setReturnForm({ condition: "Working", deduct_cost: false, deduct_amount: "", it_remark: "" });
  };
  const submitReturn = async () => {
    const { condition, deduct_cost, deduct_amount, it_remark } = returnForm;
    if (deduct_cost && (!deduct_amount || Number(deduct_amount) <= 0)) {
      toast.error("Enter a deduction amount greater than 0");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/it-assets/${returnTarget.id}/approve-return`, {
        condition,
        deduct_cost: !!deduct_cost,
        deduct_amount: deduct_cost ? Number(deduct_amount) : 0,
        it_remark,
      });
      toast.success("Asset return approved");
      setReturnTarget(null);
      loadReturns();
    } catch (err) { toast.error(formatError(err)); } finally { setSaving(false); }
  };
  const rejectReturn = async (id) => {
    if (!window.confirm("Reject this asset return request?")) return;
    try {
      await api.post(`/it-assets/${id}/reject-return`, { it_remark: "Return rejected by IT" });
      toast.success("Return request rejected");
      loadReturns();
    } catch (err) { toast.error(formatError(err)); }
  };

  // --- Asset request approval ---
  const openRequest = async (r) => {
    setRequestTarget(r);
    setPickAssetId("");
    setPickRemark("");
    try {
      const { data } = await api.get("/records/it_assets");
      // Only show assets that are truly unassigned AND in an assignable status.
      setUnassignedAssets((data || []).filter((a) =>
        !a.assigned_to_id && !a.assigned_to_name && !a.assigned_to &&
        (a.status === "Available" || !a.status)
      ));
    } catch { setUnassignedAssets([]); }
  };
  const submitRequest = async () => {
    if (!pickAssetId) { toast.error("Pick an unassigned asset"); return; }
    setSaving(true);
    try {
      await api.post(`/asset-requests/${requestTarget.id}/approve`, { asset_id: pickAssetId, remark: pickRemark });
      toast.success("Asset assigned to requester");
      setRequestTarget(null);
      loadRequests();
    } catch (err) { toast.error(formatError(err)); } finally { setSaving(false); }
  };
  const rejectRequest = async (id) => {
    const remark = window.prompt("Reason for rejection (optional):", "");
    if (remark === null) return;
    try {
      await api.post(`/asset-requests/${id}/reject`, { remark });
      toast.success("Request rejected");
      loadRequests();
    } catch (err) { toast.error(formatError(err)); }
  };

  if (!canApprove) return null;
  if (returns.length === 0 && requests.length === 0) return null;

  return (
    <div className="space-y-4 mb-5">
      {requests.length > 0 && (
        <div className="pk-card overflow-hidden border-l-4 border-violet-400" data-testid="asset-requests-section">
          <div className="px-5 py-3 bg-violet-50/60 border-b border-violet-100 flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-[11px] uppercase tracking-wider font-semibold text-violet-800 flex items-center gap-2">
                <Inbox size={14} /> IT Asset Requests · Pending Approval
              </div>
              <div className="text-[12px] text-violet-700">Pick an unassigned asset and assign it to the requester.</div>
            </div>
            <Badge className="bg-violet-600 text-white">{requests.length} pending</Badge>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/60 border-b border-slate-100">
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-2.5 font-semibold">Requester</th>
                  <th className="px-4 py-2.5 font-semibold">Product</th>
                  <th className="px-4 py-2.5 font-semibold">Cost</th>
                  <th className="px-4 py-2.5 font-semibold">Priority</th>
                  <th className="px-4 py-2.5 font-semibold">Required By</th>
                  <th className="px-4 py-2.5 font-semibold">Remark</th>
                  <th className="px-4 py-2.5 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 last:border-0" data-testid={`asset-req-row-${r.id}`}>
                    <td className="px-4 py-2.5">
                      <div className="font-semibold text-slate-800">{r.requester_name}</div>
                      <div className="text-[10px] text-slate-400 mono">{r.created_at ? new Date(r.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : ""}</div>
                    </td>
                    <td className="px-4 py-2.5 text-[12px] font-medium">{r.product_name}</td>
                    <td className="px-4 py-2.5 mono text-[12px]">{r.cost ? `₹ ${Number(r.cost).toLocaleString("en-IN")}` : "—"}</td>
                    <td className="px-4 py-2.5"><Badge className={PRIORITY_CHIP[r.priority] || "bg-slate-100"}>{r.priority}</Badge></td>
                    <td className="px-4 py-2.5 mono text-[11px] text-slate-600">{r.required_by || "—"}</td>
                    <td className="px-4 py-2.5 text-[12px] text-slate-600 max-w-xs truncate" title={r.remark}>{r.remark || "—"}</td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <Button size="sm" onClick={() => openRequest(r)} className="bg-emerald-600 hover:bg-emerald-700 h-8 text-[11px] mr-1.5" data-testid={`asset-req-approve-${r.id}`}>Approve & Assign</Button>
                      <Button size="sm" variant="outline" onClick={() => rejectRequest(r.id)} className="text-rose-700 border-rose-200 hover:bg-rose-50 h-8 text-[11px]" data-testid={`asset-req-reject-${r.id}`}>Reject</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {returns.length > 0 && (
        <div className="pk-card overflow-hidden border-l-4 border-amber-400" data-testid="asset-returns-section">
          <div className="px-5 py-3 bg-amber-50/60 border-b border-amber-100 flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-[11px] uppercase tracking-wider font-semibold text-amber-800 flex items-center gap-2">
                <PackageCheck size={14} /> IT Asset Return Requests
              </div>
              <div className="text-[12px] text-amber-700">Approve return, mark condition & raise deduction if Damaged / Non-Working.</div>
            </div>
            <Badge className="bg-amber-600 text-white">{returns.length} pending</Badge>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/60 border-b border-slate-100">
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-2.5 font-semibold">Asset</th>
                  <th className="px-4 py-2.5 font-semibold">Tag · S/N</th>
                  <th className="px-4 py-2.5 font-semibold">Requested By</th>
                  <th className="px-4 py-2.5 font-semibold">Remark</th>
                  <th className="px-4 py-2.5 font-semibold">Requested At</th>
                  <th className="px-4 py-2.5 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {returns.map((a) => (
                  <tr key={a.id} className="border-b border-slate-100 last:border-0" data-testid={`asset-return-row-${a.id}`}>
                    <td className="px-4 py-2.5">
                      <div className="font-semibold text-slate-800">{a.make_model || a.asset_type || "—"}</div>
                      <div className="text-[10px] text-slate-500">{a.asset_type}</div>
                    </td>
                    <td className="px-4 py-2.5 mono text-[11px] text-slate-600">
                      <div>{a.asset_tag}</div>
                      {a.serial_no && <div className="text-slate-400">{a.serial_no}</div>}
                    </td>
                    <td className="px-4 py-2.5 text-[12px] font-medium">{a.return_requested_by_name || a.assigned_to_name || a.assigned_to || "—"}</td>
                    <td className="px-4 py-2.5 text-[12px] text-slate-600 max-w-xs truncate" title={a.return_remark}>{a.return_remark || "—"}</td>
                    <td className="px-4 py-2.5 mono text-[11px] text-slate-500">
                      {a.return_requested_at ? new Date(a.return_requested_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                    </td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <Button size="sm" onClick={() => openReturn(a)} className="bg-emerald-600 hover:bg-emerald-700 h-8 text-[11px] mr-1.5" data-testid={`asset-return-approve-${a.id}`}>Approve</Button>
                      <Button size="sm" variant="outline" onClick={() => rejectReturn(a.id)} className="text-rose-700 border-rose-200 hover:bg-rose-50 h-8 text-[11px]" data-testid={`asset-return-reject-${a.id}`}>Reject</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Return approval dialog */}
      <Dialog open={!!returnTarget} onOpenChange={(o) => { if (!o) setReturnTarget(null); }}>
        <DialogContent className="max-w-lg">
          {returnTarget && (
            <>
              <DialogHeader><DialogTitle>Approve Asset Return</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="p-3 bg-slate-50 rounded border text-[12px]">
                  <div className="font-semibold text-slate-800">{returnTarget.make_model || returnTarget.asset_type}</div>
                  <div className="mono text-[11px] text-slate-500 mt-0.5">
                    TAG · {returnTarget.asset_tag}{returnTarget.serial_no && ` · S/N ${returnTarget.serial_no}`}
                  </div>
                  <div className="text-slate-600 mt-1.5">Returned by · <span className="font-semibold">{returnTarget.return_requested_by_name || returnTarget.assigned_to_name || returnTarget.assigned_to}</span></div>
                  {returnTarget.return_remark && <div className="text-slate-500 italic mt-1">User note: {returnTarget.return_remark}</div>}
                </div>

                <div>
                  <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Condition *</Label>
                  <Select value={returnForm.condition} onValueChange={(v) => setReturnForm((f) => ({ ...f, condition: v, deduct_cost: v === "Working" ? false : f.deduct_cost }))}>
                    <SelectTrigger className="mt-1.5" data-testid="asset-approve-condition"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CONDITIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {returnForm.condition !== "Working" && (
                  <div className="p-3 rounded-md bg-rose-50/50 border border-rose-200 space-y-3" data-testid="asset-approve-deduction-block">
                    <label className="flex items-center gap-2 text-[12px] text-rose-800 font-semibold cursor-pointer">
                      <input
                        type="checkbox"
                        checked={returnForm.deduct_cost}
                        onChange={(e) => setReturnForm((f) => ({ ...f, deduct_cost: e.target.checked }))}
                        className="rounded"
                        data-testid="asset-approve-deduct-toggle"
                      />
                      Raise a cost deduction from the employee
                    </label>
                    {returnForm.deduct_cost && (
                      <div>
                        <Label className="text-[11px] uppercase tracking-wider font-semibold text-rose-700">Deduction Amount (₹) *</Label>
                        <Input
                          type="number" min="0" step="0.01"
                          value={returnForm.deduct_amount}
                          onChange={(e) => setReturnForm((f) => ({ ...f, deduct_amount: e.target.value }))}
                          className="mt-1.5 mono"
                          placeholder="e.g. 1500"
                          data-testid="asset-approve-deduct-amount"
                        />
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">IT Remark (optional)</Label>
                  <Textarea rows={2} value={returnForm.it_remark}
                    onChange={(e) => setReturnForm((f) => ({ ...f, it_remark: e.target.value }))}
                    className="mt-1.5" placeholder="Condition notes, repair needed…"
                    data-testid="asset-approve-remark" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setReturnTarget(null)} disabled={saving}>Cancel</Button>
                <Button onClick={submitReturn} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="asset-approve-submit">
                  {saving ? "Saving…" : "Approve Return"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Asset Request → assign dialog */}
      <Dialog open={!!requestTarget} onOpenChange={(o) => { if (!o) setRequestTarget(null); }}>
        <DialogContent className="max-w-lg">
          {requestTarget && (
            <>
              <DialogHeader><DialogTitle>Approve Request &amp; Assign Asset</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="p-3 bg-slate-50 rounded border text-[12px]">
                  <div className="text-slate-500">Requester</div>
                  <div className="font-semibold text-slate-800">{requestTarget.requester_name}</div>
                  <div className="mt-1.5 text-slate-500">Requested</div>
                  <div className="font-semibold text-slate-800">{requestTarget.product_name}</div>
                  {requestTarget.remark && <div className="text-slate-500 italic mt-1">{requestTarget.remark}</div>}
                </div>

                <div>
                  <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Pick an unassigned asset *</Label>
                  <Select value={pickAssetId} onValueChange={setPickAssetId}>
                    <SelectTrigger className="mt-1.5" data-testid="asset-req-pick"><SelectValue placeholder="Select an asset…" /></SelectTrigger>
                    <SelectContent>
                      {unassignedAssets.length === 0 && <div className="px-3 py-2 text-[12px] text-slate-400">No unassigned assets — add a new asset first.</div>}
                      {unassignedAssets.map((a) => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.asset_tag} — {a.make_model || a.asset_type} {a.serial_no ? `(S/N ${a.serial_no})` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Approval Remark (optional)</Label>
                  <Textarea rows={2} value={pickRemark} onChange={(e) => setPickRemark(e.target.value)} className="mt-1.5" placeholder="Any note for the requester…" data-testid="asset-req-remark" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setRequestTarget(null)} disabled={saving}>Cancel</Button>
                <Button onClick={submitRequest} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="asset-req-submit">
                  {saving ? "Saving…" : "Approve & Assign"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
